import { RefObject, useCallback, useEffect, useMemo } from 'react';
import type { TableComponent } from '@ohpen/enso-ui';

import { drainListFilter, type ListController } from '../assistant/listActions';
import { registerListController, unregisterListController } from '../assistant/listRegistry';
import { AssistantListType, AssistantTableProps, useAssistantPageContext } from './useAssistantPageContext';
import { ListUrlStateProps, useListUrlState } from './useListUrlState';
import type { ListColumnFilters } from '@pmp/utils';

/**
 * The grid's change notification, as AntD reports it. Typed loosely on purpose: only the filter
 * selection is read here, and the handler has to stay assignable to any list's `Table` props.
 */
export type ListTableChange = (pagination: unknown, filters: ListColumnFilters, ...rest: unknown[]) => void;

/** How often a mounted list looks again for a filter its grid was not ready for. */
const DRAIN_RETRY_MS = 100;

export type ListTableStateProps<T> = AssistantTableProps<T> &
  ListUrlStateProps & {
    /** AntD's own change notification, which is the only channel a column-header filter arrives on. */
    onChange?: ListTableChange;
    /** Fires whenever the effective filter flag changes, which is how Reset Filters is caught. */
    setActiveFilters?: (activeFilters: boolean) => void;
  };

/**
 * The shared table wiring every list wants: it reports itself to the assistant, lets the assistant filter
 * it, and keeps its search text in the URL. One object to spread, so the two `onGlobalSearch` handlers
 * cannot shadow each other at the call site.
 *
 * Pass the list's `columns` when they can change after mount, so a filter on a column that is not there
 * yet is applied once it appears.
 */
export const useListTableState = <T,>(
  entityType: AssistantListType | undefined,
  rowKey: string,
  tableRef: RefObject<TableComponent | null>,
  columns?: readonly unknown[],
): ListTableStateProps<T> => {
  // Read when the turn is built rather than reported on each change: the count moves with every filter,
  // page and data load, and what the agent needs is the one the pager is showing at that moment.
  const readRowCounts = useCallback(() => tableRef.current?.getState().rows, [tableRef]);

  const assistant = useAssistantPageContext<T>(entityType, rowKey, readRowCounts);
  const urlState = useListUrlState(entityType, tableRef);

  // The grid the agent reaches when it answers with this list, registered while the list is mounted and
  // dropped on the way out, so an instruction can only ever reach a list that is on screen. What is
  // registered is a reader rather than the ref's current value: enso rebuilds its imperative handle on every
  // render, so a handle kept from mount answers for the grid as it was at mount and never changes its mind.
  //
  // A filter that arrived while the user was elsewhere is drained here as the list comes up, retrying rather
  // than running once: the grid reports no columns of its own until enso's restore has committed, a render
  // later than this, and a column an instruction names can arrive later still (Products' additional fields).
  // `drainListFilter` keeps the original deadline, so retrying cannot outlast the ten seconds the filter was
  // asked for in, and it stops itself once that passes.
  const readController = useCallback(() => (tableRef.current as unknown as ListController | null) ?? null, [tableRef]);

  useEffect(() => {
    if (entityType === undefined) return;

    registerListController(entityType, readController);

    let retry: ReturnType<typeof setTimeout> | undefined;
    const drain = (): void => {
      if (drainListFilter(entityType, readController)) return;
      retry = setTimeout(drain, DRAIN_RETRY_MS);
    };
    drain();

    return () => {
      clearTimeout(retry);
      unregisterListController(entityType, readController);
    };
  }, [columns, entityType, readController]);

  const onGlobalSearch = useCallback(
    (searchText: string) => {
      assistant.onGlobalSearch?.(searchText);
      urlState.onGlobalSearch?.(searchText);
    },
    [assistant, urlState],
  );

  // Where a filter picked in a column header lands: the grid reports it through AntD's change callback,
  // not through `onFiltersChange`, so the assistant would otherwise never hear about it.
  const onChange = useCallback<ListTableChange>((_pagination, filters) => assistant.onFiltersChange?.(filters), [assistant]);

  // The Reset Filters button clears the grid without `onChange`, so the applied snapshot is taken off the
  // ref here rather than the reader holding its own copy of that quirk.
  const setActiveFilters = useCallback(() => {
    const applied = tableRef.current?.getState().filters.byColumn;
    if (applied !== undefined) assistant.onFiltersChange?.(applied);
  }, [assistant, tableRef]);

  return useMemo(
    () => ({ ...assistant, ...urlState, onGlobalSearch, onChange, setActiveFilters }),
    [assistant, onChange, onGlobalSearch, setActiveFilters, urlState],
  );
};
